/**
 * Decimal.js utilities for precise financial calculations
 *
 * This module provides helper functions for budget calculations using decimal.js
 * to avoid JavaScript floating point precision errors.
 *
 * @see https://github.com/MikeMcl/decimal.js/
 */

import { Decimal } from 'decimal.js';

/**
 * Configure Decimal.js global settings
 * - precision: 20 significant digits (more than enough for financial calculations)
 * - rounding: ROUND_HALF_UP (0.5 rounds up to 1, matching backend behavior)
 * - toExpNeg/toExpPos: Prevent scientific notation for reasonable values
 */
Decimal.set({
  precision: 20,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -7,
  toExpPos: 21,
});

/**
 * Converts a number or string to Decimal instance
 * @param value - The value to convert
 * @returns Decimal instance
 * @throws Error if value cannot be converted to Decimal
 * @example
 * const d = D(0.1);
 * const result = d.plus(D(0.2)); // Exact: 0.3
 */
export function D(value: number | string): Decimal {
  return new Decimal(String(value));
}

/**
 * Multiplies two values with exact precision and rounds to 2 decimal places
 * @param a - First value
 * @param b - Second value
 * @returns Result rounded to 2 decimal places
 * @example
 * mult(119.75, 195) // Returns 23351.25 (exact)
 * mult(0.1, 0.2) // Returns 0.02 (exact, not 0.020000000004)
 */
export function mult(a: number | string, b: number | string): number {
  return D(a).times(D(b)).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
}

/**
 * Sums multiple values with exact precision and rounds to 2 decimal places
 * @param values - Values to sum
 * @returns Sum rounded to 2 decimal places
 * @example
 * sum(0.1, 0.2, 0.3) // Returns 0.6 (exact, not 0.6000000000000001)
 * sum(119.75, 44.50, 30.25) // Returns 194.5 (exact)
 */
export function sum(...values: (number | string)[]): number {
  const total = values.reduce((acc: Decimal, val) => acc.plus(D(val)), D(0));
  return total.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
}

/**
 * Rounds a value to 2 decimal places
 * @param value - Value to round (number, string, or Decimal)
 * @returns Value rounded to 2 decimal places
 * @example
 * round2(119.755) // Returns 119.76 (ROUND_HALF_UP)
 * round2(119.745) // Returns 119.75 (ROUND_HALF_UP)
 * round2(D(0.1).plus(D(0.2))) // Returns 0.3 (exact)
 */
export function round2(value: number | string | Decimal): number {
  const decimal = value instanceof Decimal ? value : D(value);
  return decimal.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
}
